/**
 * ColumnLogsRow — `column.logs` renderer. Typed view over a logs
 * source row's `logType` + `parentRowId` config attributes.
 *
 * Hydrated as a hidden child of any source with wire type `"logs"`
 * via the `LOGS.defaultAttributes.childSpecs` entry in
 * `row-type-specs-builtin.ts`. The parent source holds the log
 * entries as per-cell entries keyed by RFC3339 timestamps; this
 * renderer is a pure typed accessor over the config attributes
 * that drive log-entry decoding.
 */

import type { Option } from "../../core/option.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import { RendererRow } from "./renderer-row.ts";
import { SourceRow } from "./source-row.ts";
import { RowNetworkKey } from "../../ids/row-network-key.ts";

/**
 * The `column.logs` renderer row: a typed view of a logs source row's `logType` and `parentRowId`
 * attributes.
 */
export class ColumnLogsRow extends RendererRow {
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnLogsRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}).`,
      );
    }
    return p;
  }

  /**
   * Log type tag (e.g. `"weather"`, `"calendar"`). Selects the
   * per-type converter in the plugin's `ConfigRegistry`. `null`
   * when unset — caller binds an empty log list.
   */
  get logType(): Option<string> {
    return this.source.attributesAs(ColumnLogsRow.decodeAttrs)?.logType ?? null;
  }

  /** Parent row id that owns these logs. `null` when unset. */
  get parentRowId(): Option<RowNetworkKey> {
    return this.source.attributesAs(ColumnLogsRow.decodeAttrs)?.parentRowId ?? null;
  }
}

/** Types and values that go with `ColumnLogsRow`: `Attrs`, `decodeAttrs`. */
export namespace ColumnLogsRow {
  export type Attrs = Partial<{
    logType: string;
    parentRowId: RowNetworkKey;
  }>;

  /** Read a logs source's `logType` / `parentRowId` off its bag. */
  export function decodeAttrs(attributes: JsonObject): Attrs {
    const logType = Json.string(attributes, "logType");
    const parentRowId = Json.string(attributes, "parentRowId");
    return {
      ...(logType != null ? { logType } : {}),
      ...(parentRowId != null ? { parentRowId: RowNetworkKey.fromWire(parentRowId) } : {}),
    };
  }
}
